import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import debounce from "lodash/debounce";

import { activationTracking } from "app/pages/activation/services/tracking";
import { PreferencesService } from "app/services/preferences/preferencesService";

import {
    DELAY_FOR_WIDGETS_SAVE,
    WIDGET_ORDER_STORAGE_KEY,
    getWidgetsConfig,
} from "../../components/GAWidget/widgetsConfig";

export const useGAWidgetOrder = () => {
    const defaultOrder: string[] = useMemo(() => Object.keys(getWidgetsConfig()), []);
    const isFirstRender = useRef(true);
    const track = activationTracking.home;

    const getWidgetOrder = useCallback(() => {
        const savedOrder = PreferencesService.get(WIDGET_ORDER_STORAGE_KEY) as string[] | undefined;

        if (savedOrder && Array.isArray(savedOrder) && savedOrder.length === defaultOrder.length) {
            const hasAllWidgets = defaultOrder.every((widget) => savedOrder.includes(widget));

            if (hasAllWidgets) {
                return savedOrder;
            }
        }

        return defaultOrder;
    }, [defaultOrder]);

    const saveWidgetOrder = useMemo(
        () =>
            debounce((widgetOrder: string[]): void => {
                void PreferencesService.add({ [WIDGET_ORDER_STORAGE_KEY]: widgetOrder });
            }, DELAY_FOR_WIDGETS_SAVE),
        [],
    );

    const resetWidgetOrder = useCallback((): string[] => {
        void PreferencesService.remove([WIDGET_ORDER_STORAGE_KEY]);

        return defaultOrder;
    }, [defaultOrder]);

    const [widgetOrder, setWidgetOrder] = useState<string[]>(getWidgetOrder());

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;

            return;
        }

        saveWidgetOrder(widgetOrder);

        return () => {
            saveWidgetOrder(widgetOrder);
        };
    }, [widgetOrder, saveWidgetOrder]);

    const updateWidgetOrder = useCallback(
        (activeId: string, overId: string) => {
            setWidgetOrder((prevOrder) => {
                const activeIndex = prevOrder.indexOf(activeId);
                const overIndex = prevOrder.indexOf(overId);

                const movedArray = arrayMove(prevOrder, activeIndex, overIndex);

                track.trackGAWidgetChangeOrder(activeId, movedArray);

                return movedArray;
            });
        },
        [track],
    );

    const onDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            const activeId = active.id as string;
            const overId = over.id as string;

            if (activeId !== overId) {
                updateWidgetOrder(activeId, overId);
            }
        },
        [updateWidgetOrder],
    );

    const resetToDefault = useCallback(() => {
        const defaultOrder = resetWidgetOrder();
        setWidgetOrder(defaultOrder);
    }, [resetWidgetOrder]);

    return {
        widgetOrder,
        onDragEnd,
        resetToDefault,
        saveWidgetOrder,
        resetWidgetOrder,
        storageKey: WIDGET_ORDER_STORAGE_KEY,
    };
};
